import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  message,
  Switch,
  Radio,
  Select,
  Upload,
  Modal,
  InputNumber,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import Spinner from "react-bootstrap/Spinner";
import CodeVerification from "../Common/components/CodeVerification";
import AddressAutocomplete from "./AddressAutocomplete";
import * as com from "../Common.js";
// import "./App.css";
// import "./Prequal.css";
import "./QuickIntake.css";

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  ssn?: string;
  ficoScore?: number;
  hasCoBorrower: boolean;
  // Co-borrower fields
  coFirstName?: string;
  coLastName?: string;
  coEmail?: string;
  coPhone?: string;
  coSsn?: string;
  coFicoScore?: number;
  loanPurpose: string;
  loanAmount?: number;
  downPayment?: number;
  isFirstTimeBuyer?: string;
  remainingBalance?: number;
  currentInterestRate?: number;
  hasSecondMortgage?: string;
  borrowAdditionalCash?: number;
  loanType: string;
  propertyType: string;
  purchasePrice: number;
  creditScore: string;
  closingDate?: any;
  notes?: string;
  // Property address fields
  propertyAddress?: string;
  propertyUnit?: string;
  propertyState?: string;
  propertyCounty?: string;
  propertyCity?: string;
  propertyZip?: string;
  propertyValue?: number;
  propertyUse?: string;
  declaration_A?: string;
  declaration_B?: string;
  declaration_C?: string;
  declaration_D?: string;
  declaration_E?: string;
  declaration_F?: string;
  declaration_G?: string;
  declaration_H?: string;
  declaration_I?: string;
  declaration_J?: string;
  declaration_K?: string;
  declaration_L?: string;
  declaration_M?: string;
  declaration_D1?: string;
  declaration_D2?: string;
  declaration_A1?: string;
  declaration_C1?: string;
  declaration_A1_Type?: string;
  declaration_A1_Status?: string;
}

const QuickIntake: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm<FormValues>();
  const [verifyEmail, setVerifyEmail] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [loanPurpose, setLoanPurpose] = useState<string>("purchase");
  const [showCoBorrower, setShowCoBorrower] = useState<boolean>(false);

  // 从URL获取loId
  const loId = new URLSearchParams(location.search).get("loId") || "";

  const [declarationA, setDeclarationA] = useState<string>("");
  const [declarationC, setDeclarationC] = useState<string>("");
  const [declarationA1, setDeclarationA1] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("");
  const [addressValue, setAddressValue] = useState<string>("");
  const addressValueRef = useRef<string>("");
  const [fileList, setFileList] = useState([]);
  const [loanId, setLoanId] = useState<string>("");
  const [formData, setFormData] = useState<FormValues | null>(null);
  const [loanLimits, setLoanLimits] = useState({});
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  // 获取贷款限额数据
  useEffect(() => {
    fetchLoanLimits();
  }, []);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [timer]);

  const fetchLoanLimits = () => {
    fetch("/data/loanlimits")
      .then((res) => {
        if (!res.ok) {
          throw Error(res.statusText);
        }
        return res.json();
      })
      .then((res) => {
        setLoanLimits(res);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  // State and County options
  const getStateOptions = () => {
    let options = [];
    options.push({ value: "", label: "Select State" });
    
    if (!loanLimits) {
      return options;
    }

    let keys = Object.keys(loanLimits);
    keys.forEach((x) => {
      options.push({
        value: com.capitalize(x),
        label: com.capitalize(x),
      });
    });
    
    return options;
  };

  const getCountyOptions = (state: string) => {
    let options = [];
    options.push({ value: "", label: "Select County" });
    
    if (!state || !loanLimits) {
      return options;
    }

    let s = state.toUpperCase();
    if (state.length === 2) {
      s = com.convertAbbStateToFull(state).toUpperCase();
    }

    if (s in loanLimits) {
      let keys = Object.keys(loanLimits[s]);
      keys.forEach((x) => {
        options.push({ value: com.capitalize(x), label: com.capitalize(x) });
      });
    }
    
    return options;
  };

  // Email verification states
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [verified, setVerified] = useState<boolean>(false);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [verifyingCode, setVerifyingCode] = useState<boolean>(false);
  const [sendingCode, setSendingCode] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [lastVerifiedCode, setLastVerifiedCode] = useState<string>(""); // 防止重复验证同一个验证码
  const [emailVerified, setEmailVerified] = useState<boolean>(false); // 跟踪邮箱是否已验证

  // SSN隐私开关状态
  const [showSSN, setShowSSN] = useState<boolean>(false);
  const [showCoSSN, setShowCoSSN] = useState<boolean>(false);

  // 眼睛图标组件
  const EyeIcon = ({ visible, onClick }: { visible: boolean; onClick: () => void }) => {
    return (
      <div 
        onClick={onClick}
        style={{ 
          cursor: 'pointer', 
          display: 'flex', 
          alignItems: 'center',
          padding: '4px'
        }}
      >
        {visible ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="19" viewBox="0 0 22 19" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M11 2.69141C7.40678 2.69141 4.28619 5.29082 2.33569 9.21134C2.30378 9.27558 2.28015 9.32306 2.26022 9.36446C2.24265 9.40091 2.23175 9.42487 2.22461 9.44141C2.23175 9.45794 2.24265 9.48191 2.26022 9.51836C2.28015 9.55976 2.30378 9.60723 2.33569 9.67147C4.28619 13.592 7.40678 16.1914 11 16.1914C14.5932 16.1914 17.7138 13.592 19.6643 9.67147C19.6962 9.60723 19.7199 9.55976 19.7398 9.51836C19.7573 9.48191 19.7683 9.45794 19.7754 9.44141C19.7683 9.42487 19.7573 9.40091 19.7398 9.36446C19.7199 9.32306 19.6962 9.27558 19.6643 9.21134C17.7138 5.29082 14.5932 2.69141 11 2.69141ZM0.350225 8.19941C2.50617 3.86594 6.24258 0.441406 11 0.441406C15.7574 0.441406 19.4939 3.86594 21.6497 8.19941C21.6576 8.21516 21.6656 8.23113 21.6739 8.24767C21.7753 8.45028 21.9064 8.71184 21.9651 9.03427C22.0116 9.29077 22.0116 9.59204 21.9651 9.84854C21.9064 10.171 21.7753 10.4326 21.6739 10.6351C21.6656 10.6517 21.6576 10.6677 21.6497 10.6834C19.4939 15.0169 15.7574 18.4414 11 18.4414C6.24258 18.4414 2.50617 15.0169 0.350225 10.6834C0.342434 10.6677 0.334387 10.6517 0.326139 10.6351C0.224638 10.4325 0.093582 10.171 0.034962 9.84854C-0.011654 9.59204 -0.011654 9.29077 0.034962 9.03427C0.093582 8.71184 0.224638 8.45028 0.326139 8.24767C0.334387 8.23113 0.342434 8.21516 0.350225 8.19941ZM11 7.19141C9.77224 7.19141 8.77701 8.19873 8.77701 9.44141C8.77701 10.6841 9.77224 11.6914 11 11.6914C12.2277 11.6914 13.223 10.6841 13.223 9.44141C13.223 8.19873 12.2277 7.19141 11 7.19141ZM6.55405 9.44141C6.55405 6.95612 8.54457 4.94141 11 4.94141C13.4555 4.94141 15.446 6.95612 15.446 9.44141C15.446 11.9266 13.4555 13.9414 11 13.9414C8.54457 13.9414 6.55405 11.9266 6.55405 9.44141Z" fill="#6E6E70"/>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="18" viewBox="0 0 22 18" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M1.77538 0.237027C1.29584 -0.144424 0.604735 -0.056067 0.231759 0.434363C-0.141216 0.924804 -0.0548221 1.63161 0.424721 2.01307L2.93023 4.00609C1.96279 5.11351 1.13371 6.39052 0.460393 7.75803L0.436545 7.80629C0.336094 8.00879 0.206405 8.27035 0.148391 8.59288C0.102246 8.84938 0.102246 9.15066 0.148391 9.40704C0.206405 9.72958 0.336094 9.99114 0.436545 10.1938L0.460393 10.242C2.59404 14.5755 6.29181 18 11.0001 18C13.4544 18 15.6341 17.0694 17.4609 15.5646L20.2246 17.7629C20.7042 18.1444 21.3952 18.0561 21.7682 17.5656C22.1412 17.0752 22.0548 16.3684 21.5753 15.9869L1.77538 0.237027ZM15.6651 14.1361L13.6877 12.5631C12.9443 13.1506 12.0121 13.5 11.0001 13.5C8.56997 13.5 6.60004 11.4852 6.60004 9.00002C6.60004 8.36473 6.72874 7.76028 6.96092 7.21229L4.66912 5.38928C3.81323 6.34471 3.05723 7.48656 2.42532 8.76996C2.39374 8.83408 2.37035 8.88167 2.35062 8.92307C2.33324 8.95952 2.32244 8.98348 2.31539 9.00002C2.32244 9.01656 2.33324 9.04052 2.35062 9.07697C2.37035 9.11837 2.39374 9.16584 2.42532 9.23008C4.35565 13.1506 7.44395 15.75 11.0001 15.75C12.6845 15.75 14.264 15.1667 15.6651 14.1361ZM8.82053 8.69155C8.807 8.79235 8.80007 8.89539 8.80007 9.00002C8.80007 10.2427 9.785 11.25 11.0001 11.25C11.2933 11.25 11.5733 11.1913 11.8291 11.0847L8.82053 8.69155Z" fill="#6E6E70"/>
            <path d="M10 5.13683L14.9994 9C14.9998 8.97836 15 8.95662 15 8.93497C15 6.76174 13.2267 5 11.0392 5C10.6796 5 10.3311 5.0476 10 5.13683Z" fill="#6E6E70"/>
            <path d="M19.6919 9.39996C19.312 10.1868 18.8872 10.9216 18.4227 11.594L20.1557 13C20.7172 12.1967 21.2182 11.333 21.6539 10.4305L21.6777 10.3814C21.778 10.175 21.9075 9.90866 21.9654 9.58018C22.0115 9.31908 22.0115 9.01226 21.9654 8.75104C21.9075 8.42256 21.778 8.15619 21.6777 7.94996L21.6539 7.90081C19.5235 3.48757 15.8312 0 11.1301 0C9.24612 0 7.52428 0.560055 6 1.5153L7.89926 3.0562C8.91386 2.5604 9.99826 2.29141 11.1301 2.29141C14.6808 2.29141 17.7644 4.93868 19.6919 8.93137C19.7235 8.99668 19.7469 9.04514 19.7665 9.0873C19.7839 9.12442 19.7946 9.14883 19.8017 9.16567C19.7946 9.18251 19.7839 9.20691 19.7665 9.24403C19.7469 9.2862 19.7235 9.33454 19.6919 9.39996Z" fill="#6E6E70"/>
          </svg>
        )}
      </div>
    );
  };

  // 格式化SSN的函数
  const formatSSN = (value: string) => {
    const ssn = value.replace(/[^\d]/g, "");
    const ssnLength = ssn.length;
    if (ssnLength < 4) return ssn;
    if (ssnLength < 6) {
      return `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
    }
    return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
  };

  // 处理SSN显示格式的函数
  const getSSNDisplayValue = (value: string, isVisible: boolean) => {
    if (!value) return "";
    if (isVisible) {
      return formatSSN(value);
    } else {
      // 当不可见时，显示***-**-****格式
      const ssn = value.replace(/[^\d]/g, "");
      const ssnLength = ssn.length;
      if (ssnLength === 0) return "";
      if (ssnLength < 4) return "***";
      if (ssnLength < 6) return `***-${"*".repeat(ssnLength - 3)}`;
      return `***-**-${"*".repeat(Math.min(ssnLength - 5, 4))}`;
    }
  };

  // 自定义SSN输入组件
  const SSNAntdInput = ({ 
    value, 
    onChange, 
    placeholder, 
    maxLength, 
    isVisible, 
    onToggleVisibility 
  }: {
    value?: string;
    onChange?: (e: any) => void;
    placeholder?: string;
    maxLength?: number;
    isVisible: boolean;
    onToggleVisibility: () => void;
  }) => {
    const [displayValue, setDisplayValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    // 更新显示值
    useEffect(() => {
      if (isFocused) {
        setDisplayValue(value || "");
      } else {
        setDisplayValue(getSSNDisplayValue(value || "", isVisible));
      }
    }, [value, isVisible, isFocused]);

    const handleChange = (e: any) => {
      const newValue = e.target.value;
      setDisplayValue(newValue);
      if (onChange) {
        onChange(e);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      setDisplayValue(value || "");
    };

    const handleBlur = () => {
      setIsFocused(false);
      setDisplayValue(getSSNDisplayValue(value || "", isVisible));
    };

    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <Input
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={maxLength}
          style={{ paddingRight: '40px' }}
        />
        <div 
          style={{ 
            position: 'absolute', 
            right: '8px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            cursor: 'pointer'
          }}
          onClick={onToggleVisibility}
        >
          <EyeIcon visible={isVisible} onClick={onToggleVisibility} />
        </div>
      </div>
    );
  };

  // 验证SSN长度的函数
  const checkSSNLength = (_, value: string) => {
    if (value && value.replace(/[^\d]/g, "").length !== 9) {
      return Promise.reject(new Error('Please enter a 9-digit Social security number'));
    }
    return Promise.resolve();
  };

  // 验证电话号码格式的函数
  const validatePhoneNumber = (_, value: string) => {
    const regex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (value && !regex.test(value)) {
      return Promise.reject(new Error('Please enter a valid phone number'));
    }
    return Promise.resolve();
  };

  const onFinish = async (values: FormValues) => {
    setLoading(true);

    try {
      // 保存表单值到组件状态，以便后续使用
      setFormData(values);

      // 判断邮箱是否已经验证过
      if (emailVerified) {
        // 如果已经验证过，直接保存数据
        await saveStep1Data(values);
        // message.success("Application data saved successfully!");
        setCurrentStep(2);
      } else {
        // 如果还没验证过，先验证后保存
        setEmail(values.email);
        setVerifyEmail(true);
        await sendVerificationCode(values.email);
      }
    } catch (error) {
      message.error("Submission failed, please try again");
    } finally {
      setLoading(false);
    }
  };

  const codes = () => {
    return (
      <CodeVerification
        value={verificationCode}
        onChange={(code: string) => {
          // console.log("--------Code onChange:", code);
          setVerificationCode(code);
          // 当输入完成时自动验证，且不是重复验证同一个验证码
          if (code.length === 4 && code !== lastVerifiedCode) {
            // console.log("--------Code onCompleted:", code);
            verifyCode(code);
          }
        }}
      />
    );
  };

  const sendVerificationCode = async (email: string) => {
    setSendingCode(true);
    try {
      const response = await fetch("/los/generateonetimepasscode", {
        cache: "no-cache",
        method: "POST",
        body: JSON.stringify({
          Email: email,
        }),
        headers: {
          "Content-Type": "application/json",
          Cache: "no-cache",
        },
      });

      if (response.status !== 200) {
        console.log(
          "Looks like there was a problem. Status Code: " + response.status
        );
        message.error("Failed to send verification code, please try again");
        return;
      }

      // message.success("Verification code sent to your email");
    } catch (error) {
      console.log(error);
      message.error("Failed to send verification code, please try again");
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async (code: string) => {
    setVerifyingCode(true);
    setLastVerifiedCode(code); // 记录已验证的验证码
    try {
      // TODO: 临时使用 /los/verifycodeandsignin 接口，后续需要改为专门的验证接口
      const response = await fetch("/los/verifycodeandsignin", {
        cache: "no-cache",
        method: "POST",
        body: JSON.stringify({
          Email: email,
          Code: code,
          // loId: loId,
          // CustomerID:
          //   new URLSearchParams(location.search).get("customerId") || "", // 从URL查询参数获取customerId
        }),
        headers: {
          // "Content-Type": "application/json",
          Cache: "no-cache",
        },
      });

      if (response.status !== 200) {
        console.log(
          "Looks like there was a problem. Status Code: " + response.status
        );
        setShowResult(true);
        setVerified(false);
        setVerificationCode("");
        message.error("Verification failed, please try again");
        return;
      }

      const result = await response.json();

      if (result.Status !== "OK") {
        console.log(result);
        setShowResult(true);
        setVerified(false);
        setVerificationCode("");
        message.error("Invalid or expired verification code");
        return;
      } else {
      // TODO: 这里暂时忽略登录逻辑，只验证code
      // 后续可能需要处理Token和State的情况
      setVerified(true);
      setEmailVerified(true); // 标记邮箱已验证
      setShowResult(true);
      message.success("Email verification successful!");

      // 验证成功时清空验证码
      setVerificationCode("");

      // 保存第一步数据
      await saveStep1Data(formData);

      // 继续到下一步
      let timer = setTimeout(() => {
        setVerifyEmail(false);
        setCurrentStep(2);
      }, 2000);
      setTimer(timer);

      }
    } catch (error) {
      console.log(error);
      message.error("Verification failed, please try again");
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleResendCode = () => {
    sendVerificationCode(email);
  };

  const handleEditEmail = () => {
    // 如果已经验证通过，不允许编辑邮箱
    if (emailVerified) {
      message.warning("Email cannot be modified after verification");
      return;
    }
    
    // 清理定时器
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
    
    setVerifyEmail(false);
    setVerified(false);
    setShowResult(false);
    setVerificationCode("");
    setLastVerifiedCode(""); // 重置已验证的验证码
  };

  // 文件上传相关函数
  const isHeicOrHeif = (fileName) => {
    return /\.(heic|heif)$/i.test(fileName);
  };

  const draggerProps = {
    fileList: fileList,
    multiple: true,
    showUploadList: false,
    onChange(info) {
      const fileList = [...info.fileList];
      setFileList(fileList);
    },
    onDrop(e) {},
    beforeUpload: async (file) => {
      if (isHeicOrHeif(file?.name?.toLowerCase())) {
        message.warning("You can only upload image and PDF files.");
        return Upload.LIST_IGNORE;
      } else {
        // const allowedFileTypes = ["image/jpeg", "image/png", "application/pdf"];
        // const fileType = file.type;
        // const isAllowed = allowedFileTypes.includes(fileType);
        // if (!isAllowed) {
        //   message.warning("You can only upload image and PDF files.");
        //   return Upload.LIST_IGNORE;
        // }
      }
      return true;
    },
  };

  const handleDeleteDoc = (index) => {
    const updatedFile = [...fileList];
    updatedFile.splice(index, 1);
    setFileList(updatedFile);
  };

  const removeFileExtension = (fileName) => {
    return fileName.replace(/\.[^/.]+$/, "");
  };

  // 保存第一步数据到数据库
  const saveStep1Data = async (formValues?: FormValues) => {
    try {
      // 如果没有传入 formValues，则从 form 实例获取
      let values = formValues || form.getFieldsValue();
      
      // 调试信息：检查获取到的数据
      console.log("=== saveStep1Data Debug ===");
      console.log("formValues passed:", formValues);
      console.log("form.getFieldsValue() result:", form.getFieldsValue());
      console.log("final values used:", values);

      // 如果使用form.getFieldsValue()，需要手动补充一些可能缺失的字段
      if (!formValues) {
        // 补充地址相关字段，这些字段可能因为AddressAutocomplete的自定义处理而缺失
        const addressFields = {
          propertyAddress: addressValueRef.current || form.getFieldValue("propertyAddress") || "",
          propertyState: selectedState || form.getFieldValue("propertyState") || "",
          propertyCounty: form.getFieldValue("propertyCounty") || "",
          propertyCity: form.getFieldValue("propertyCity") || "",
          propertyZip: form.getFieldValue("propertyZip") || "",
        };
        values = { ...values, ...addressFields };
        console.log("补充后的values:", values);
      }

      // 构建第一步的数据
      let step1Data = {
        // Basic borrower information
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        ssn: values.ssn,
        ficoScore: Number(values.ficoScore || ""),
        hasCoBorrower: !!values.hasCoBorrower,

        // Co-borrower information
        coFirstName: values.coFirstName,
        coLastName: values.coLastName,
        coEmail: values.coEmail,
        coPhone: values.coPhone,
        coSsn: values.coSsn,
        coFicoScore: Number(values.coFicoScore || ""),

        // Loan information
        loanPurpose: values.loanPurpose,
        loanAmount: Number(values.loanAmount || ""),
        downPayment: Number(values.downPayment || ""),
        isFirstTimeBuyer: values.isFirstTimeBuyer,
        remainingBalance: Number(values.remainingBalance || ""),
        currentInterestRate: Number(values.currentInterestRate || ""),
        hasSecondMortgage: values.hasSecondMortgage === "yes" ? 1 : 0,
        borrowAdditionalCash: Number(values.borrowAdditionalCash || ""),

        // Property information
        propertyAddress: values.propertyAddress,
        propertyUnit: values.propertyUnit,
        propertyState: values.propertyState,
        propertyCounty: values.propertyCounty,
        propertyCity: values.propertyCity,
        propertyZip: values.propertyZip,
        propertyValue: Number(values.propertyValue || ""),
        propertyUse: values.propertyUse,

        // Customer ID from URL
        customerId:
          new URLSearchParams(location.search).get("customerId") || "",
        // Loan Officer ID from URL
        loanOfficerId: loId,
      };
      // 只有在更新现有贷款时才添加loanId
      if(loanId) {
        // @ts-ignore
        step1Data.loanId = loanId;
      }

      console.log("step1Data----------", step1Data);
      console.log("propertyAddress value:", step1Data.propertyAddress);
      console.log("addressValueRef.current:", addressValueRef.current);
      console.log("form.getFieldValue('propertyAddress'):", form.getFieldValue("propertyAddress"));

      const response = await fetch("/borrower/createquickapplication", {
        cache: "no-cache",
        method: "POST",
        body: JSON.stringify(step1Data),
        headers: {
          "Content-Type": "application/json",
          Cache: "no-cache",
        },
      });

      if (response.status !== 200) {
        console.log("Save step1 data failed. Status Code: " + response.status);
        message.error("Failed to save application data, please try again");
        return;
      }

      const result = await response.json();

      if (result.status === "OK" && result.loan_id) {
        setLoanId(result.loan_id);
        console.log("Step1 data saved successfully, LoanID:", result.loan_id);
      } else {
        console.log("Save step1 data failed:", result);
        message.error("Failed to save application data, please try again");
      }
    } catch (error) {
      console.log("Save step1 data error:", error);
      message.error("Failed to save application data, please try again");
    }
  };

  const log_svg = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="187"
        height="40"
        viewBox="0 0 187 40"
        fill="none"
      >
        <path
          d="M110.434 8.69106C111.594 8.69106 112.571 8.28811 113.375 7.48221C114.175 6.67631 114.574 5.68592 114.574 4.51591C114.574 3.3459 114.175 2.35551 113.375 1.54961C112.576 0.743712 111.594 0.335907 110.434 0.335907C109.273 0.335907 108.296 0.738857 107.492 1.54961C106.692 2.35066 106.293 3.3459 106.293 4.51591C106.293 5.68592 106.692 6.67145 107.492 7.48221C108.291 8.28325 109.273 8.69106 110.434 8.69106Z"
          fill="#081265"
        />
        <path
          d="M113.963 10.7338H106.905V36.2508H113.963V10.7338Z"
          fill="#081265"
        />
        <path
          d="M128.309 15.8564V27.2895C128.309 28.2702 128.559 29.0373 129.064 29.5907C129.565 30.1442 130.157 30.4209 130.851 30.4209C132.165 30.4209 133.277 29.8189 134.187 28.6198L136.821 33.6008C134.625 35.6253 132.31 36.6351 129.883 36.6351C127.456 36.6351 125.396 35.834 123.72 34.2174C122.045 32.6056 121.207 30.4063 121.207 27.6245V15.8613H118.241V10.7395H121.207V3.09796H128.309V10.7346H134.423V15.8564H128.309Z"
          fill="#081265"
        />
        <path
          d="M153.145 16.9858C151.046 16.9858 149.476 17.7383 148.441 19.2384C147.406 20.7386 146.89 22.7242 146.89 25.1904V36.24H139.832V10.7279H146.89V14.0972C147.796 13.0534 148.937 12.1649 150.299 11.4416C151.662 10.7134 153.048 10.3347 154.459 10.3007L154.507 16.9858H153.145Z"
          fill="#081265"
        />
        <path
          d="M67.8096 10.779H54.9884L51.0435 16.7458H63.8646L51.0435 36.1377H72.2476L76.1926 30.171H63.3715L76.1926 10.779H67.8096Z"
          fill="#081265"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M169.632 36.633C176.849 36.633 182.7 30.7337 182.7 23.4564C182.7 16.1791 176.849 10.2798 169.632 10.2798C162.415 10.2798 156.564 16.1791 156.564 23.4564C156.564 30.7337 162.415 36.633 169.632 36.633ZM169.632 29.9204C173.173 29.9204 176.043 27.0263 176.043 23.4564C176.043 19.8864 173.173 16.9924 169.632 16.9924C166.092 16.9924 163.222 19.8864 163.222 23.4564C163.222 27.0263 166.092 29.9204 169.632 29.9204Z"
          fill="#081265"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M93.8226 36.0273C97.5345 35.0245 100.417 32.8382 102.477 29.4671L97.5479 26.3359C96.4335 28.3708 94.6778 29.7148 92.2869 30.3608C90.8086 30.7602 89.3602 30.6941 87.9519 30.1599C86.5437 29.6257 85.554 28.7117 84.9824 27.4239L102.794 22.6115L101.82 18.9444C100.87 15.3711 98.9502 12.8741 96.0587 11.4638C93.1664 10.0539 90.0443 9.80183 86.6812 10.7105C83.3191 11.6189 80.7076 13.5508 78.8457 16.5065C76.9907 19.4655 76.5709 22.8761 77.6027 26.759C78.6344 30.6416 80.6651 33.4053 83.7053 35.0567C86.7406 36.7044 90.1116 37.03 93.8226 36.0273ZM83.8753 22.5072C83.2246 20.0586 85.0832 17.1619 88.1086 16.3445C91.1339 15.5271 94.1799 17.0987 94.8306 19.5473L83.8753 22.5072Z"
          fill="#081265"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M12.9622 33.6141L11.9494 36.6779C11.4637 38.147 12.5482 39.6642 14.084 39.6642H35.4619C37.9472 39.6642 39.962 37.6327 39.962 35.1267L39.9626 10.9263C39.9626 8.42023 37.9479 6.38863 35.4625 6.38863H30.9618L24.9617 24.5389H34.1118L31.1118 33.6141H12.9622Z"
          fill="#1630AC"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M12.967 33.6115H8.46688C5.98156 33.6115 3.96681 31.5799 3.96681 29.0739V4.87349C3.96681 2.36746 5.98156 0.335915 8.46688 0.335915H29.8455C31.3813 0.335915 32.4657 1.85304 31.9801 3.32215L30.9672 6.38601H12.967L9.96691 15.4612H18.9671L12.967 33.6115Z"
          fill="#081265"
        />
      </svg>
    );
  };

  const ProgressBar = ({ currentStep }) => {
    const totalSteps = 3;
    const progressPercentage = (currentStep / totalSteps) * 100;

    return (
      <div
        style={{
          width: "100%",
          backgroundColor: "#EAEFFD",
          height: "8px",
          borderRadius: "100px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${progressPercentage}%`,
            backgroundColor: "#325CEB",
            height: "100%",
            borderRadius: "100px",
            transition: "width 0.3s ease-in-out",
            position: "relative",
          }}
        />
        {/* 步骤分隔线 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "33.33%",
            width: "8px",
            height: "100%",
            backgroundColor: "#EAEFFD",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "66.66%",
            width: "8px",
            height: "100%",
            backgroundColor: "#EAEFFD",
            zIndex: 1,
          }}
        />
      </div>
    );
  };

  const Basic = () => {
    return (
      <div>
        <div className="title-basic">Basic</div>
        <div className="title-borrower">Borrower</div>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: "Please enter first name" }]}
            >
              <Input placeholder="Enter first name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: "Please enter last name" }]}
            >
              <Input placeholder="Enter last name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input 
                placeholder="Enter email address" 
                disabled={emailVerified}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[
                { required: true, message: "Please enter phone number" },
                { validator: validatePhoneNumber }
              ]}
              getValueFromEvent={(e) => com.fixPhoneInput(e.target.value)}
            >
              <Input 
                placeholder="(###) ###-####" 
                maxLength={14}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              label="SSN" 
              name="ssn"
              rules={[
                { validator: checkSSNLength }
              ]}
              getValueFromEvent={(e) => formatSSN(e.target.value)}
            >
              <SSNAntdInput 
                placeholder="###-##-####" 
                maxLength={11}
                isVisible={showSSN}
                onToggleVisibility={() => setShowSSN(!showSSN)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="FICO Score" name="ficoScore">
              <InputNumber
                style={{ width: '100%', height: "46px", borderRadius: "8px" }}
                placeholder="Enter FICO score"
                min={0}
                max={1000}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="" name="hasCoBorrower" valuePropName="checked">
              <div 
                style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  cursor: "pointer",
                  // padding: "8px 0",
                  userSelect: "none"
                }} 
                onClick={() => {
                  const newValue = !showCoBorrower;
                  setShowCoBorrower(newValue);
                  form.setFieldsValue({ hasCoBorrower: newValue });
                }}
              >
                <Switch 
                  checked={showCoBorrower}
                  onChange={(checked) => {
                    setShowCoBorrower(checked);
                    form.setFieldsValue({ hasCoBorrower: checked });
                  }}
                  style={{ 
                    marginRight: "10px"
                  }}
                />
                <Text
                  style={{
                    fontSize: "16px",
                    color: "#222222",
                    fontWeight: "400",
                    userSelect: "none",
                    flex: 1
                  }}
                >
                  I have a co-borrower
                </Text>
              </div>
            </Form.Item>
          </Col>
        </Row>

        {showCoBorrower && (
          <>
            <div style={{ marginTop: "0px", marginBottom: "20px" }}>
              <div className="title-borrower">Co-Borrower</div>
              {/* <div>Co-Borrower Information</div> */}
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="First Name"
                  name="coFirstName"
                  rules={[
                    {
                      required: true,
                      message: "Please enter co-borrower first name",
                    },
                  ]}
                >
                  <Input placeholder="Enter co-borrower first name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Last Name"
                  name="coLastName"
                  rules={[
                    {
                      required: true,
                      message: "Please enter co-borrower last name",
                    },
                  ]}
                >
                  <Input placeholder="Enter co-borrower last name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="coEmail"
                  rules={[
                    {
                      required: true,
                      message: "Please enter co-borrower email",
                    },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input placeholder="Enter co-borrower email address" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Phone Number"
                  name="coPhone"
                  rules={[
                    {
                      required: true,
                      message: "Please enter co-borrower phone number",
                    },
                    { validator: validatePhoneNumber }
                  ]}
                  getValueFromEvent={(e) => com.fixPhoneInput(e.target.value)}
                >
                  <Input 
                    placeholder="(###) ###-####" 
                    maxLength={14}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  label="SSN" 
                  name="coSsn"
                  rules={[
                    { validator: checkSSNLength }
                  ]}
                  getValueFromEvent={(e) => formatSSN(e.target.value)}
                >
                  <SSNAntdInput 
                    placeholder="###-##-####" 
                    maxLength={11}
                    isVisible={showCoSSN}
                    onToggleVisibility={() => setShowCoSSN(!showCoSSN)}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="FICO Score" name="coFicoScore">
                  <InputNumber
                    style={{ width: '100%', height: "46px", borderRadius: "8px" }}
                    placeholder="Enter co-borrower FICO score"
                    min={0}
                    max={1000}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="I would like to"
              name="loanPurpose"
              initialValue="purchase"
              // rules={[
              //   { required: true, message: "Please select loan purpose" },
              // ]}
            >
              <Radio.Group onChange={(e) => setLoanPurpose(e.target.value)}>
                <Radio value="purchase">Purchase</Radio>
                <Radio value="refinance">Refinance</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {loanPurpose === "purchase" && (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Loan Amount"
                  name="loanAmount"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Enter loan amount"
                    prefix="$"
                    formatter={value => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value ? value.replace(/\$|,/g, '') : ''}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Down Payment"
                  name="downPayment"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Enter down payment"
                    prefix="$"
                    formatter={value => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value ? value.replace(/\$|,/g, '') : ''}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="First time buyer?"
                  name="isFirstTimeBuyer"
                >
                  <Radio.Group>
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {loanPurpose === "refinance" && (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Remaining balance on the mortgage"
                  name="remainingBalance"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Enter remaining balance"
                    prefix="$"
                    formatter={value => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value ? value.replace(/\$|,/g, '') : ''}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Current interest rate"
                  name="currentInterestRate"
                >
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.01}
                    style={{ width: '100%', height: '46px', borderRadius: '8px' }}
                    suffix="%"
                    placeholder="Enter current interest rate"
                    // No formatter: one that rewrites the value on every
                    // keystroke (toFixed(2)) fought the cursor while typing,
                    // and rounding to two places turned an eighth like 5.875
                    // into 5.88. min/max still clamp on blur.
                    stringMode
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Have second mortgage?"
                  name="hasSecondMortgage"
                >
                  <Radio.Group>
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Borrow additional cash"
                  name="borrowAdditionalCash"
                >
                  <InputNumber
                    style={{ width: '100%', height: '46px', borderRadius: '8px' }}
                    placeholder="Enter additional cash amount"
                    prefix="$"
                    formatter={value => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value ? value.replace(/\$|,/g, '') : ''}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </div>
    );
  };

  const Property = () => {
    return (
      <div>
        <div className="title-basic">Subject Property</div>
        {/* <div>Property Information</div> */}

        <Row gutter={16}>
          <Col span={18}>
            <Form.Item
              label={<div className="item-label">Address</div>}
              name="propertyAddress"
              style={{ marginBottom: 0 }}
            >
              <AddressAutocomplete
                id="PropertyAddress"
                required={false}
                border={true}
                defaultValue={addressValue}
                hideLabel={true}
                onChange={(e) => {
                  const newValue = e.target.value;
                  addressValueRef.current = newValue;
                  // 当用户手动输入时，清空其他相关字段
                  if (!newValue) {
                    form.setFieldsValue({
                      propertyState: "",
                      propertyCounty: "",
                      propertyCity: "",
                      propertyZip: "",
                    });
                    setSelectedState("");
                  }
                }}
                pattern="^[0-9]+\\s[\\p{L}\\p{N} !:,&()'?#+\\/@-]+$"
                placeholder="address"
                goodfeedback="Looks good!"
                badfeedback="Please provide your street address."
                onCompletion={(address) => {
                  let state = com.getSubaddress(
                    address,
                    "administrative_area_level_1",
                    "short_name"
                  );
                  let zip = com.getSubaddress(
                    address,
                    "postal_code",
                    "long_name"
                  );
                  let county = com.getSubaddress(
                    address,
                    "administrative_area_level_2",
                    "long_name"
                  );
                  let num = com.getSubaddress(
                    address,
                    "street_number",
                    "long_name"
                  );
                  let short = com.getSubaddress(address, "route", "short_name");
                  let city = com.getSubaddress(address, "locality", "long_name");
                  let stradd =
                    num + " " + short + ", " + city + ", " + state + " " + zip;

                  const fullStateName = com.capitalize(
                    com.convertAbbStateToFull(state)
                  );
                  setSelectedState(fullStateName);

                  // 设置地址状态和表单字段值
                  setAddressValue(stradd);
                  addressValueRef.current = stradd;
                  form.setFieldsValue({
                    propertyState: fullStateName,
                    propertyCounty: county,
                    propertyCity: city,
                    propertyZip: zip,
                    propertyAddress: stradd,
                  });
                }}
                size="md"
                style={{
                  height: "46px",
                  borderRadius: "8px",
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item 
              label="Unit" 
              name="propertyUnit"
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="Unit" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              label="State"
              name="propertyState"
            >
              <Select
                placeholder="Select State"
                onChange={(value) => {
                  setSelectedState(value);
                  // 清空县的选择，因为州改变了
                  form.setFieldsValue({ propertyCounty: "" });
                }}
              >
                {getStateOptions().map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="County"
              name="propertyCounty"
            >
              <Select placeholder="Select County">
                {getCountyOptions(selectedState).map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="City"
              name="propertyCity"
            >
              <Input placeholder="Enter city" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Zip"
              name="propertyZip"
            >
              <Input placeholder="Enter zip code" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Property Value"
              name="propertyValue"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Enter property value"
                prefix="$"
                formatter={value => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                parser={value => value ? value.replace(/\$|,/g, '') : ''}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Property Use"
              name="propertyUse"
            >
              <Select placeholder="Select property use">
                <Option value="primary">Primary Residence</Option>
                <Option value="secondary">Secondary Home</Option>
                <Option value="investment">Investment</Option>
                <Option value="fha">FHA Secondary Residence</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </div>
    );
  };

  const Step1 = () => {
    return (
      <div>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Basic></Basic>
          <Property></Property>
          <Form.Item style={{ textAlign: "right", marginTop: "30px" }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              style={{
                minWidth: "120px",
                backgroundColor: "#1F3988",
                borderColor: "#1F3988",
              }}
            >
              Next
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  };



  const VerifyEmail = () => {
    const showErrorMessage = () => {
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            style={{
              backgroundColor: "#FFE6BF",
              borderRadius: "4px",
              padding: "10px",
              marginTop: "15px",
              maxWidth: "350px",
            }}
          >
            <img
              src="/images/warningSignYellow.png"
              style={{ width: "18px", height: "18px", marginRight: "10px" }}
            />
            <span>The code is incorrect or expired.</span>
          </div>
        </div>
      );
    };

    if (showResult && verified) {
      return (
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <div style={{ textAlign: "left", marginBottom: "-40px" }}>
            <Button className="button-back" onClick={() => {
                setVerifyEmail(false);
                setCurrentStep(1);
                // 重置表单到第一步
                // form.resetFields();
                // 清除定时器
                clearTimeout(timer);
                setTimer(null);
              }}
            >
              Back
            </Button>
          </div>
          <img
            // src="/images/emailSuccess.png"
            src="/images/checkEmail2.png"
            style={{ width: "80px", height: "78px" }}
          />
          <div
            style={{
              fontFamily: "Inter",
              fontStyle: "normal",
              fontWeight: 700,
              fontSize: "26px",
              lineHeight: "normal",
              marginTop: "23px",
              marginBottom: "6px",
            }}
          >
            Verified!
          </div>
          <div
            style={{
              color: "#6E6E70",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              marginTop: "10px",
              marginBottom: "12px",
            }}
          >
            <div>Thank you for verifying your email</div>
            <div
              className="title-continue"
              onClick={() => {
                setCurrentStep(2);
                setVerifyEmail(false);
              }}
            >
              Continue to your application.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <div style={{ textAlign: "left", marginBottom: "-40px" }}>
          <Button className="button-back" onClick={() => {
              setVerifyEmail(false);
              setCurrentStep(1);
              // 重置表单到第一步
              // form.resetFields();
              // 清除定时器
              clearTimeout(timer);
              setTimer(null);
            }}
          >
            Back
          </Button>
        </div>
        <div>
          <img
            src="/images/checkEmail2.png"
            style={{ width: 66, height: "auto", marginBottom: "23px" }}
          />
        </div>
        <div
          style={{
            color: "#222",
            fontSize: 26,
            fontFamily: "Playfair Display",
            lineHeight: "35px",
            fontWeight: 700,
            marginBottom: "10px",
          }}
        >
          We need to verify your email
        </div>

        {email && (
          <div
            style={{
              margin: "6px auto 18px",
              color: "#6E6E70",
              lineHeight: "20px",
            }}
          >
            <div>
              We sent a code to{" "}
              <span style={{ fontWeight: 600, color: "#222" }}>{email}</span>
              {!emailVerified && (
                <a
                  style={{
                    color: "#325CEB",
                    marginLeft: 8,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={handleEditEmail}
                >
                  Edit
                </a>
              )}
            </div>
            <div>Enter the code to activate your account</div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "20px 0 0 10px",
          }}
        >
          {verifyingCode || sendingCode ? (
            <div style={{ padding: "20px" }}>
              {/* @ts-ignore */}
              <Spinner animation="border" size="lg" />
            </div>
          ) : (
            codes()
          )}
        </div>

        {showResult && !verified && showErrorMessage()}

        <div
          style={{
            fontSize: 16,
            padding: "8px",
            fontWeight: 400,
            color: "#6e6e70",
          }}
        >
          Didn't receive the email?
          <a
            style={{
              color: "#325CEB",
              textDecoration: "underline",
              cursor: "pointer",
              marginLeft: "8px",
            }}
            onClick={handleResendCode}
          >
            Resend
          </a>
          {showResult && !verified && (
            <>
              {" "}
              or{" "}
              <a
                style={{
                  color: "#325CEB",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setVerificationCode("");
                  setLastVerifiedCode("");
                  setShowResult(false);
                }}
              >
                Try again
              </a>
            </>
          )}
        </div>
      </div>
    );
  };

  const Step2 = () => {
    const declarations = [
      "A. Will you occupy the property as your primary residence?",
      "B. If this is a Purchase Transaction: Do you have a family relationship or business affiliation with the seller of the property?",
      "C. Are you borrowing any money for this real estate transaction (e.g., money for your closing costs or down payment) or obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?",
      "D1. Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?",
      "D2. Have you or will you be applying for any new credit (e.g., installment loan, credit card, etc.) on or before closing this loan that is not disclosed on this application?",
      "E. Will this property be subject to a lien that could take priority over the first mortgage lien, such as a clean energy lien paid through your property taxes (e.g., the Property Assessed Clean Energy Program)?",
      "F. Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application? ",
      "G. Are there any outstanding judgments against you?",
      "H. Are you currently delinquent or in default on a Federal debt?",
      "I. Are you a party to a lawsuit in which you potentially have any personal financial liability?",
      "J. Have you conveyed title to any property in lieu of foreclosure in the past 7 years?",
      "K. Within the past 7 years, have you completed a pre-foreclosure sale or short sale, whereby the property was sold to a third party and the Lender agreed to accept less than the outstanding mortgage balance due?",
      "L. Have you had property foreclosed upon in the last 7 years?",
      "M. Have you declared bankruptcy within the past 7 years?",
    ];

    // 声明名称映射数组，确保索引对应正确的声明名称
    const declarationNames = ['A', 'B', 'C', 'D1', 'D2', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];

    const handleBack = () => {
      // 如果邮箱已验证，直接回到第一步
      // 如果邮箱未验证，回到邮箱验证页面
      if (emailVerified) {
        setCurrentStep(1);
      } else {
        setVerifyEmail(true);
      }
    };

    const handleNext = async () => {
      try {
        // 验证表单
        await form.validateFields();

        // 保存第二步数据
        await saveStep2Data();

      } catch (error) {
        console.log("Step2 validation failed:", error);
      }
    };

    return (
      <div>
        <div className="title-declaration">Declarations</div>
        {/* <div>Please answer the following questions:</div> */}

        <Form form={form} layout="vertical">
          {declarations.map((question, index) => (
            <div key={index} style={{ marginBottom: "20px" }}>
              <div
                className="title-question"
                // style={{
                //   fontWeight: 500,
                //   marginBottom: "8px",
                //   color: "#333",
                // }}
              >
                {question}
              </div>
              <Form.Item
                name={`declaration_${declarationNames[index]}`}
                rules={[{ required: true, message: "Please select an answer" }]}
                className="text-answer"
              >
                <Radio.Group
                  onChange={(e) => {
                    if (index === 0) setDeclarationA(e.target.value);
                    if (index === 2) setDeclarationC(e.target.value);
                  }}
                >
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </Radio.Group>
              </Form.Item>

              {/* 问题A为yes时的额外问题 */}
              {index === 0 && declarationA === "yes" && (
                <div
                  style={{
                    marginTop: "15px",
                    // paddingLeft: "20px",
                    // borderLeft: "2px solid #EAEFFD",
                  }}
                >
                  <div style={{ marginBottom: "15px" }}>
                    <div
                      className="title-question"
                      // style={{
                      //   fontWeight: 500,
                      //   marginBottom: "8px",
                      //   color: "#333",
                      // }}
                    >
                      Have you had an ownership interest in another property in
                      the last three years?
                    </div>
                    <Form.Item
                      name="declaration_A1"
                      rules={[
                        { required: true, message: "Please select an answer" },
                      ]}
                      className="text-answer"
                    >
                      <Radio.Group
                        onChange={(e) => setDeclarationA1(e.target.value)}
                      >
                        <Radio value="yes">Yes</Radio>
                        <Radio value="no">No</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </div>

                  {declarationA1 === "yes" && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <div
                          className="title-question"
                          // style={{
                          //   fontWeight: 500,
                          //   marginBottom: "8px",
                          //   color: "#333",
                          // }}
                        >
                          What type of property did you own?
                        </div>
                        <Form.Item
                          name="declaration_A1_Type"
                          rules={[
                            {
                              required: true,
                              message: "Please select property type",
                            },
                          ]}
                          className="text-answer"
                        >
                          <Select placeholder="Select property type">
                            <Option value="primary">Primary residence</Option>
                            <Option value="secondary">Secondary home</Option>
                            <Option value="investment">Investment</Option>
                            <Option value="fha">FHA secondary residence</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <div
                          className="title-question"
                          // style={{
                          //   fontWeight: 500,
                          //   marginBottom: "8px",
                          //   color: "#333",
                          // }}
                        >
                          How did you hold title to the property?
                        </div>
                        <Form.Item
                          name="declaration_A1_Status"
                          rules={[
                            {
                              required: true,
                              message: "Please select property status",
                            },
                          ]}
                          className="text-answer"
                        >
                          <Select placeholder="Select property status">
                            <Option value="by-yourself">By yourself</Option>
                            <Option value="jointly-with-spouse">
                              Jointly with your spouse
                            </Option>
                            <Option value="jointly-with-other">
                              Jointly with another person
                            </Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </div>
              )}

              {/* 问题C为yes时的额外问题 */}
              {index === 2 && declarationC === "yes" && (
                <div
                  style={{
                    marginTop: "15px",
                    // paddingLeft: "20px", borderLeft: "2px solid #EAEFFD"
                  }}
                >
                  <div
                    className="title-question"
                    // style={{
                    //   fontWeight: 500,
                    //   marginBottom: "8px",
                    //   color: "#333",
                    // }}
                  >
                    What is the amount of this money?
                  </div>
                  <Form.Item
                    name="declaration_C1"
                    rules={[
                      { required: true, message: "Please enter the amount" },
                    ]}
                    className="text-answer"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Enter amount"
                      prefix="$"
                      formatter={value => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                      parser={value => value ? value.replace(/\$|,/g, '') : ''}
                    />
                  </Form.Item>
                </div>
              )}
            </div>
          ))}

          <Form.Item>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                marginTop: "30px",
                marginBottom: 0,
              }}
            >
              <Button size="large" onClick={handleBack} className="button-back">
                Back
              </Button>
              <Button
                style={{
                  minWidth: "120px",
                  backgroundColor: "#1F3988",
                  borderColor: "#1F3988",
                }}
                type="primary"
                size="large"
                onClick={handleNext}
                className="button-next"
              >
                Next
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    );
  };

  const Step3 = () => {
    const handleBack = () => {
      setCurrentStep(2);
    };

    const handleSubmit = async () => {
      if (!loanId) {
        message.error("Loan ID not found. Please restart the application.");
        return;
      }

      if (fileList.length === 0) {
        message.warning(
          "Please upload at least one document before submitting."
        );
        return;
      }

      try {
        // 上传文件
        const formData = new FormData();
        fileList.forEach((item) => {
          formData.append(`upload`, item.originFileObj);
        });

        // 获取 token
        const token = sessionStorage.getItem("ZeitroA") || "";

        const response = await fetch("/docs/uploaddocsfromquickapplication", {
          cache: "no-cache",
          method: "POST",
          body: formData,
          headers: {
            // Authorization: "Bearer " + token,
            Cache: "no-cache",
            // "X-TASKID": "2296", // 对于 QuickIntake，可能不需要特定的 TaskID
            "X-LOAN": loanId, // 使用 loanId
            "X-CustomerID": new URLSearchParams(location.search).get("customerId") || "",
            "X-LOID": loId, // 添加 Loan Officer ID
          },
        });

        if (response.status !== 200) {
          console.log(
            "Upload documents failed. Status Code: " + response.status
          );
          message.error("Failed to upload documents, please try again");
          return;
        }

        const result = await response.json();

        if (result.Status === "OK") {
          message.success("Application submitted successfully!");
          // TODO: 这里可以添加提交成功后的跳转逻辑
          setCurrentStep(4);
        } else {
          console.log("Upload documents failed:", result);
          message.error("Failed to upload documents, please try again");
        }
      } catch (error) {
        console.log("Upload documents error:", error);
        message.error("Failed to upload documents, please try again");
      }
    };

    return (
      <div>
        <div className="title-upload">Document Upload</div>
        {/* <div style={{ marginBottom: "20px" }}>
          Please prepare the following documents for your application:
        </div> */}

        <div style={{ marginBottom: "30px" }}>
          <ul style={{ paddingLeft: "20px" }}>
            <li style={{ marginBottom: "8px" }}>
              All paystubs in the last 30 days
            </li>
            <li style={{ marginBottom: "8px" }}>
              The past 2 years W2s, 1099s, etc...
            </li>
            <li style={{ marginBottom: "8px" }}>
              Self-employed personal and business tax returns with all pages for
              the past 2 years
            </li>
            <li style={{ marginBottom: "8px" }}>
              The last 60 days of bank and/or investment account statements
              along with clarification of any large deposits
            </li>
            <li style={{ marginBottom: "8px" }}>
              Clear copy of driver license
            </li>
            <li style={{ marginBottom: "8px" }}>
              Most recent homeowners insurance and / or HOA bill, as applicable
            </li>
            <li style={{ marginBottom: "8px" }}>
              A complete signed purchase agreement, if applicable
            </li>
          </ul>
        </div>

        {/* 文件上传区域 */}
        <div style={{ marginBottom: "30px" }}>
          {/* <div style={{ marginBottom: "12px", fontWeight: 500, color: "#333" }}>
            Upload Documents
          </div> */}
          <Dragger {...draggerProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined rev={undefined} />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
          </Dragger>
          {/* <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
            Accepted formats: PDF, JPEG, JPG, PNG
          </div> */}

          {/* 文件列表 */}
          {fileList.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <div
                style={{ marginBottom: "8px", fontWeight: 500, color: "#333" }}
              >
                Uploaded Files:
              </div>
              <div>
                {fileList.map((item, index) => (
                  <div
                    key={item.uid}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      marginBottom: "4px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ marginRight: "8px" }}>
                        {item.type === "application/pdf" ? "📄" : "🖼️"}
                      </span>
                      <span>{removeFileExtension(item.name)}</span>
                    </div>
                    <span
                      onClick={() => handleDeleteDoc(index)}
                      style={{
                        cursor: "pointer",
                        opacity: 0.6,
                        fontSize: "16px",
                      }}
                    >
                      ✕
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "30px",
          }}
        >
          <Button size="large" onClick={handleBack} className="button-back">
            Back
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            className="button-submit"
          >
            Submit
          </Button>
        </div>
      </div>
    );
  };

  // 保存第二步数据到数据库
  const saveStep2Data = async () => {
    try {
      const formValues = form.getFieldsValue();

      // 构建第二步的数据
      const step2Data = {
        // Declarations
        declaration_A: formValues.declaration_A,
        declaration_B: formValues.declaration_B,
        declaration_C: formValues.declaration_C,
        declaration_D1: formValues.declaration_D1,
        declaration_D2: formValues.declaration_D2,
        declaration_E: formValues.declaration_E,
        declaration_F: formValues.declaration_F,
        declaration_G: formValues.declaration_G,
        declaration_H: formValues.declaration_H,
        declaration_I: formValues.declaration_I,
        declaration_J: formValues.declaration_J,
        declaration_K: formValues.declaration_K,
        declaration_L: formValues.declaration_L,
        declaration_M: formValues.declaration_M,

        // 额外问题 (当 declaration_A = "yes" 时)
        declaration_A1: formValues.declaration_A1,
        declaration_A1_Type: formValues.declaration_A1_Type,
        declaration_A1_Status: formValues.declaration_A1_Status,

        // 额外问题 (当 declaration_C = "yes" 时)
        declaration_C1: Number(formValues.declaration_C1 || ""),

        // 贷款ID (从第一步响应获取)
        customerId:
          new URLSearchParams(location.search).get("customerId") || "",
        loanId: loanId || "",
        // Loan Officer ID from URL
        // loId: loId,
      };

      console.log("step2Data----------", step2Data);
      console.log("formValues.declaration_D1:", formValues.declaration_D1);
      console.log("formValues.declaration_D2:", formValues.declaration_D2);
      console.log("formValues.declaration_E:", formValues.declaration_E);
      console.log("formValues.declaration_F:", formValues.declaration_F);
      console.log("All formValues:", formValues);

      const response = await fetch("/borrower/createquickapplicationdeclaration", {
        cache: "no-cache",
        method: "POST",
        body: JSON.stringify(step2Data),
        headers: {
          "Content-Type": "application/json",
          Cache: "no-cache",
        },
      });

      if (response.status !== 200) {
        console.log("Save step2 data failed. Status Code: " + response.status);
        message.error("Failed to save application data, please try again");
        return;
      }

      const result = await response.json();

      if (result.status === "OK") {
        console.log("Step2 data saved successfully");
        setCurrentStep(3);
      } else {
        console.log("Save step2 data failed:", result);
        message.error("Failed to save application data, please try again");
      }
    } catch (error) {
      console.log("Save step2 data error:", error);
      message.error("Failed to save application data, please try again");
    }
  };

  const Step4 = () => {
    return <div>
      <div className="title-thank-you">Thank you!</div>
      <div className="text-thank-you">Your loan officer will reach out to you soon.</div>
    </div>;
  };

  // 在组件内顶部添加 parseRate 函数
  const parseRate = (value: string) => {
    if (!value) return undefined;
    const parsed = parseFloat(value.replace(/[^\d.]/g, ''));
    if (isNaN(parsed)) return undefined;
    if (parsed < 0) return 0;
    if (parsed > 100) return 100;
    return Math.round(parsed * 100) / 100;
  };

  return (
    <div
      className="quick-intake-container"
      style={{ minHeight: "100vh", backgroundColor: "#eef1f4", width: "100%" }}
    >
      <div
        className="content-wrapper"
        style={{ padding: "19px 26px", width: "900px", margin: "0 auto" }}
      >
        {verifyEmail ? (
          <Card
            style={{
              // marginTop: "20px",
              // maxWidth: "500px",
              // minHeight: "500px",
              margin: "20px auto",
              border: "none",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "left",
                marginBottom: "20px",
              }}
            >
              {log_svg()}
            </div>
            {VerifyEmail()}
          </Card>
        ) : (
          <Card
            style={{
              // minHeight: "500px",
              // marginTop: "20px",
              //   boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              // border: "none",
            }}
          >
            <div style={{ display: "flex", justifyContent: "left" }}>
              {log_svg()}
            </div>

            {currentStep !== 4 &&
              <>
                <div style={{ marginBottom: "35px", marginTop: "29px" }}>
                  {ProgressBar({ currentStep })}
                </div>

                <Title level={2} className="title-quick-application">
                  Quick Application
                </Title>
              </>
            }


            {currentStep === 1 && Step1()}
            {currentStep === 2 && Step2()}
            {currentStep === 3 && Step3()}
            {currentStep === 4 && Step4()}
          </Card>
        )}
      </div>
    </div>
  );
};

export default QuickIntake;

// ==================== API 接口示例 ====================

// 第一步数据保存接口示例 (/los/savequickintake)
export const step1DataExample = {
  // 基本信息
  firstName: "John",
  lastName: "Doe",
  email: "john.doe@example.com",
  phone: "555-123-4567",
  ssn: "123-45-6789",
  ficoScore: 750,
  hasCoBorrower: true,

  // 共同借款人信息
  coFirstName: "Jane",
  coLastName: "Doe",
  coEmail: "jane.doe@example.com",
  coPhone: "555-987-6543",
  coSsn: "987-65-4321",
  coFicoScore: 780,

  // 贷款信息
  loanPurpose: "purchase", // "purchase" | "refinance"
  loanAmount: 300000,
  downPayment: 60000,
  isFirstTimeBuyer: "yes", // "yes" | "no"
  remainingBalance: 1000, // 仅再融资时使用
  currentInterestRate: 7, // 仅再融资时使用
  hasSecondMortgage: 100000, // "yes" | "no", 仅再融资时使用
  borrowAdditionalCash: 2000, // 仅再融资时使用

  // 房产信息
  propertyAddress: "123 Main St, New York, NY 10001",
  propertyUnit: "Apt 4B",
  propertyState: "New York",
  propertyCounty: "New York County",
  propertyCity: "New York",
  propertyZip: "10001",
  propertyValue: 400000,
  propertyUse: "primary", // "primary" | "secondary" | "investment" | "fha"

  // 客户ID (从URL参数获取)
  customerId: "gmcc",
};

// 第一步响应示例
export const step1ResponseExample = {
  status: "OK",
  loan_id: "LOAN789012",
  Message: "Step1 data saved successfully",
};

// 第二步数据保存接口示例 (/los/savequickintake) - 与第一步使用同一个接口
export const step2DataExample = {
  // 声明问题 A-M
  declaration_A: "yes", // "yes" | "no"
  declaration_B: "no",
  declaration_C: "no",
  declaration_D1: "no", // 问题D1
  declaration_D2: "no", // 问题D2
  declaration_E: "no",
  declaration_F: "no",
  declaration_G: "no",
  declaration_H: "no",
  declaration_I: "no",
  declaration_J: "no",
  declaration_K: "no",
  declaration_L: "no",
  declaration_M: "no",

  // 额外问题 (当 declaration_A = "yes" 时)
  declaration_A1: "yes", // "yes" | "no"
  declaration_A1_Type: "primary", // "primary" | "secondary" | "investment" | "fha"
  declaration_A1_Status: "by-yourself", // "by-yourself" | "jointly-with-spouse" | "jointly-with-other"

  // 额外问题 (当 declaration_C = "yes" 时)
  declaration_C1: 1000, // 金额数值

  // 贷款ID (从第一步响应获取)
  loanId: "LOAN789012",
};

// 第二步响应示例
export const step2ResponseExample = {
  status: "OK",
  Message: "Step2 data saved successfully",
};

// 第三步文件上传接口示例 (/docs/uploaddocumentsforrequest)
export const step3DataExample = {
  // FormData 格式
  // upload: [文件1 - bank_statement.pdf]
  // upload: [文件2 - w2_2023.pdf]
  // upload: [文件3 - driver_license.jpg]

  // 请求头
  headers: {
    Authorization: "Bearer [token]",
    Cache: "no-cache",
    "X-TASKID": "", // 对于 QuickIntake，可能不需要特定的 TaskID
    "X-LOAN": "LOAN789012", // 使用 loanId
  },

  // 支持的文件格式
  supportedFormats: ["PDF", "JPEG", "JPG", "PNG"],

  // 示例文件列表
  fileList: [
    {
      name: "bank_statement.pdf",
      type: "application/pdf",
      size: 1024000,
    },
    {
      name: "w2_2023.pdf",
      type: "application/pdf",
      size: 512000,
    },
    {
      name: "driver_license.jpg",
      type: "image/jpeg",
      size: 256000,
    },
  ],

  // 贷款ID
  loanId: "LOAN789012",
};

// 第三步响应示例
export const step3ResponseExample = {
  Status: "OK",
  Message: "Documents uploaded successfully",
  UploadedFiles: [
    {
      fileName: "bank_statement.pdf",
      fileSize: 1024000,
      uploadTime: "2024-01-15T10:30:00Z",
    },
    {
      fileName: "w2_2023.pdf",
      fileSize: 512000,
      uploadTime: "2024-01-15T10:30:05Z",
    },
    {
      fileName: "driver_license.jpg",
      fileSize: 256000,
      uploadTime: "2024-01-15T10:30:10Z",
    },
  ],
};

// 接口调用流程
export const apiFlowExample = {
  step1: {
    url: "/los/savequickintake",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Cache: "no-cache",
    },
    body: "step1DataExample",
    response: "step1ResponseExample",
    description: "保存第一步数据（基本信息、贷款信息、房产信息），返回LoanID",
  },
  step2: {
    url: "/los/savequickintake", // 与第一步使用同一个接口
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Cache: "no-cache",
    },
    body: "step2DataExample",
    response: "step2ResponseExample",
    description: "保存第二步数据（声明问题），使用第一步返回的LoanID",
  },
  step3: {
    url: "/docs/uploaddocumentsforrequest",
    method: "POST",
    headers: {
      Authorization: "Bearer [token]",
      Cache: "no-cache",
      "X-TASKID": "", // 对于 QuickIntake，可能不需要特定的 TaskID
      "X-LOAN": "[loanId]", // 使用 loanId
    },
    body: "FormData with files",
    response: "step3ResponseExample",
    description: "上传文件，使用第一步返回的LoanID，需要Authorization token",
  },
};

// 